import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { 
  Wrench, 
  Star, 
  MapPin, 
  Phone, 
  Shield,
  ArrowLeft,
  Globe,
  Clock,
  User,
  MessageSquare,
  CheckCircle,
  TrendingUp,
  Zap,
  AlertCircle,
  Lock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MechanicDetailPage = () => {
  const { mechanicId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mechanic, setMechanic] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [mechanicId]);

  const fetchData = async () => {
    try {
      const [mechanicRes, reviewsRes] = await Promise.all([
        axios.get(`${API_URL}/api/mechanics/${mechanicId}`),
        axios.get(`${API_URL}/api/mechanics/${mechanicId}/reviews`)
      ]);
      setMechanic(mechanicRes.data);
      setReviews(reviewsRes.data);
    } catch (error) {
      console.error('Error fetching mechanic:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!mechanic) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
        <Wrench className="w-12 h-12 text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-900 mb-2">Mechanic not found</h2>
        <Button onClick={() => navigate('/mechanics')}>Back to Mechanics</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img src="/logo.png" alt="Mechanicease" className="h-14 w-auto" />
            </Link>
            <div className="flex items-center gap-4">
              {user ? (
                <Button onClick={() => navigate('/dashboard')}>Dashboard</Button>
              ) : (
                <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={() => navigate('/register')}>
                  Get Started
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/mechanics" className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-6" data-testid="back-link">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Mechanics
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header Card */}
            <Card className="bg-white border border-slate-200" data-testid="mechanic-header">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h1 className="font-['Barlow'] text-2xl md:text-3xl font-bold text-slate-900">
                        Verified Mechanic
                      </h1>
                      {mechanic.is_verified && (
                        <Badge className="bg-green-100 text-green-800 border border-green-200">
                          <Shield className="w-3 h-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                      {mechanic.is_bilingual && (
                        <Badge className="bg-blue-100 text-blue-800 border border-blue-200">
                          Bilingual
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            className={`w-5 h-5 ${i < Math.round(mechanic.rating || 0) ? 'text-yellow-500 fill-yellow-500' : 'text-slate-300'}`} 
                          />
                        ))}
                      </div>
                      <span className="font-semibold">{(mechanic.rating || 0).toFixed(1)}</span>
                      <span>({mechanic.total_reviews || 0} reviews)</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 text-slate-600">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-slate-400" />
                    <span>{mechanic.service_zone || 'Available in your area'}</span>
                  </div>
                  
                  {/* Shop name, address & contact info hidden until booking */}
                  <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <Lock className="w-5 h-5 text-amber-500" />
                    <div>
                      <span className="text-sm text-amber-800 font-medium">Identity Protected</span>
                      <p className="text-xs text-amber-600">Shop name, address, and contact details are revealed only after booking confirmation to ensure all services go through Mechanicease.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-slate-400" />
                    <span>{mechanic.years_experience || '5+'} years experience</span>
                  </div>
                  
                  {mechanic.is_bilingual && mechanic.languages?.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Globe className="w-5 h-5 text-slate-400" />
                      <span>Languages: {mechanic.languages.join(', ')}</span>
                    </div>
                  )}
                </div>

                {/* No Unnecessary Delays */}
                <div className="mt-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-5 h-5 text-orange-600" />
                    <span className="font-bold text-orange-800">No Unnecessary Delays</span>
                  </div>
                  <p className="text-sm text-orange-700">
                    This mechanic commits to completing repairs within estimated timeframes 
                    and providing clear explanations for any extended repair times.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Performance Metrics */}
            <Card className="bg-white border border-slate-200" data-testid="performance-metrics">
              <CardHeader>
                <CardTitle className="font-['Barlow'] text-xl flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  Performance Metrics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-700">
                      {mechanic.performance_metrics?.on_time_completion_rate || 100}%
                    </p>
                    <p className="text-xs text-green-600">On-Time Completion</p>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-700">
                      {mechanic.performance_metrics?.response_time_hours || 0.5}h
                    </p>
                    <p className="text-xs text-blue-600">Avg Response Time</p>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <p className="text-2xl font-bold text-purple-700">
                      {mechanic.performance_metrics?.jobs_completed || 0}
                    </p>
                    <p className="text-xs text-purple-600">Jobs Completed</p>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <p className="text-2xl font-bold text-orange-700">
                      {100 - (mechanic.performance_metrics?.dispute_rate || 0)}%
                    </p>
                    <p className="text-xs text-orange-600">Satisfaction Rate</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Services */}
            <Card className="bg-white border border-slate-200" data-testid="mechanic-services">
              <CardHeader>
                <CardTitle className="font-['Barlow'] text-xl">Services Offered</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {mechanic.services.map((service, idx) => (
                    <Badge key={idx} variant="outline" className="bg-slate-50 border-slate-200 text-slate-700 py-1.5 px-3">
                      {service}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Reviews */}
            <Card className="bg-white border border-slate-200" data-testid="mechanic-reviews">
              <CardHeader>
                <CardTitle className="font-['Barlow'] text-xl">Customer Reviews</CardTitle>
              </CardHeader>
              <CardContent>
                {reviews.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-600">No reviews yet</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {reviews.map((review) => (
                      <div key={review.review_id} className="border-b border-slate-100 last:border-0 pb-6 last:pb-0">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-slate-600" />
                            </div>
                            <span className="font-medium text-slate-900">{review.customer_name}</span>
                          </div>
                          <div className="flex items-center">
                            {[...Array(5)].map((_, i) => (
                              <Star 
                                key={i} 
                                className={`w-4 h-4 ${i < review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-slate-300'}`} 
                              />
                            ))}
                          </div>
                        </div>
                        <p className="text-slate-600">{review.comment}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Booking Card */}
            <Card className="bg-white border border-slate-200 sticky top-24" data-testid="booking-card">
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <p className="text-slate-500 text-sm">Experience</p>
                  <p className="font-['Barlow'] text-3xl font-bold text-orange-600">{mechanic.years_experience || 5}+ years</p>
                  {mechanic.is_bilingual && (
                    <p className="text-blue-600 text-sm mt-2">Bilingual: {mechanic.languages?.join(', ')}</p>
                  )}
                </div>

                {/* Why book through us */}
                <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-xs text-green-700 text-center">
                    <CheckCircle className="w-3 h-3 inline mr-1" />
                    Book through Mechanicease to get invoice tracking, dispute protection, and quality assurance
                  </p>
                </div>

                {user && user.role === 'customer' ? (
                  <Button 
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white h-12 font-semibold"
                    onClick={() => navigate(`/book/${mechanic.mechanic_id}`)}
                    data-testid="book-now-btn"
                  >
                    Book Now
                  </Button>
                ) : user ? (
                  <p className="text-center text-slate-500 text-sm">
                    Only customers can book services
                  </p>
                ) : (
                  <div className="space-y-3">
                    <Button 
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white h-12 font-semibold"
                      onClick={() => navigate('/register')}
                      data-testid="signup-to-book-btn"
                    >
                      Sign Up to Book
                    </Button>
                    <p className="text-center text-slate-500 text-sm">
                      Already have an account?{' '}
                      <Link to="/login" className="text-orange-500 hover:text-orange-600">Login</Link>
                    </p>
                  </div>
                )}

                <div className="mt-6 pt-6 border-t border-slate-200">
                  <div className="flex items-center gap-2 text-green-600 text-sm">
                    <Shield className="w-4 h-4" />
                    <span>Verified & Trusted Mechanic</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MechanicDetailPage;
